import React, { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import Autocomplete from "veneer/AutocompleteSelectInput";
import Box from "veneer/Box";
import DataTable, {
  createColumnHelper,
  createFilter,
  sorting,
} from "veneer/DataTable";
import DestructiveConfirmationDialog from "veneer/DestructiveConfirmationDialog";
import { DeleteIconButton } from "veneer/IconButton";
import List from "veneer/List";
import ListItem from "veneer/ListItem";
import Stack from "veneer/Stack";

import { DELETE_SCHEDULED_DEPLOYMENT } from "cortex/ui/src/fleet/redux-store/scheduled-deployments/actions";
import {
  scheduledDeploymentsSelector,
  scheduledDeploymentsStoresLoadedSelector,
} from "cortex/ui/src/fleet/redux-store/scheduled-deployments/selectors";
import { Deployment, Target } from "cortex/ui/src/fleet/view/strategy/model";
import { useAppDispatch, useAppSelector } from "cortex/ui/src/hooks";
import { createPromiseApiAction } from "cortex/ui/src/redux-store/api-sagas/actions";
import { makeGetAllAsArray } from "cortex/ui/src/redux-store/selectors";
import { LoadingIndicator } from "cortex/ui/src/ui/loader";
import { Option } from "cortex/ui/src/ui/table/types";
import {
  DEFAULT_TIME_FORMAT_WITH_TZ,
  formatDateWithAllTimezones,
  utcToLocalFormat,
} from "cortex/ui/src/util/time";
import { Container } from "cortex/ui/src/view/layout";
import { VIEW } from "cortex/ui/src/view/views";

type Filter = {
  strategy?: string | null;
  target?: ReadonlyArray<string>;
};

type DeploymentRow = Deployment & {
  targetName: string;
};

const getAllDeploymentsAsArray = makeGetAllAsArray<Deployment>();
const filter = createFilter<DeploymentRow>("strategy", "targetName");
const column = createColumnHelper<DeploymentRow>();

export default function ScheduleDeployList() {
  const dispatch = useAppDispatch();
  const deploymentsById = useAppSelector(scheduledDeploymentsSelector);
  const deployments = getAllDeploymentsAsArray({ byId: deploymentsById });
  const rows: DeploymentRow[] = useMemo(
    () =>
      deployments.map((deployment: Deployment) => ({
        ...deployment,
        targetName: (deployment.targets ?? [])
          .map((target: Target) => target?.name)
          .filter(Boolean)
          .join(" "),
      })),
    [deployments],
  );
  const storesLoaded = useAppSelector(scheduledDeploymentsStoresLoadedSelector);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [filters, setFilters] = useState<Filter>({
    strategy: null,
    target: [],
  });
  const [toDelete, setToDelete] = useState<Deployment | null>(null);

  const strategyOptions: Option<string>[] = useMemo(() => {
    const set = new Set<string>();
    for (const d of deployments) {
      if (d.strategy) {
        set.add(d.strategy);
      }
    }
    return Array.from(set)
      .sort((a, b) => a.localeCompare(b))
      .map((s) => ({ label: s, value: s }));
  }, [deployments]);

  const targetOptions: Option<string>[] = useMemo(() => {
    const idToName = new Map<string, string>();
    for (const d of deployments) {
      for (const t of d.targets ?? []) {
        if (t?.id != null && t?.name) {
          idToName.set(String(t.id), t.name);
        }
      }
    }
    return Array.from(idToName.entries())
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([id, name]) => ({ label: name, value: id }));
  }, [deployments]);

  const columns = useMemo(
    () => [
      column.accessor("strategy", {
        header: "Strategy",
        cell: (prop) => {
          const row = prop.row.original;
          const value = prop.getValue();
          return (
            <Link to={`/v2/strategies/${row.strategy_id}/${VIEW.EDIT}`}>
              {value}
            </Link>
          );
        },
        sortingFn: "alphanumeric",
      }),
      column.accessor("targets", {
        header: "Targets",
        cell: (prop) => {
          return formatTargets(prop.getValue());
        },
        enableSorting: false,
      }),
      column.accessor("start_date", {
        header: "Deploy Date",
        cell: (prop) => {
          const row = prop.row.original;
          if (row.deployed_immediately) {
            return formatDateTime(row.create_date);
          }
          return formatDateWithAllTimezones(prop.getValue());
        },
        sortingFn: sorting.datetime((row) => row.start_date),
      }),
      column.accessor("end_date", {
        header: "End Date",
        cell: (prop) => formatDateWithAllTimezones(prop.getValue()),
        sortingFn: sorting.datetime((row) => row.end_date),
      }),
      column.display({
        id: "operations",
        header: "Operations",
        cell: (prop) => {
          return (
            <DeleteIconButton onClick={() => openDelete(prop.row.original)} />
          );
        },
        meta: {
          width: 150,
        },
      }),
    ],
    [],
  );

  const openDelete = useCallback((d: Deployment) => {
    setToDelete(d);
    setDeleteOpen(true);
  }, []);

  const closeDelete = useCallback(() => {
    setToDelete(null);
    setDeleteOpen(false);
  }, []);

  const handleDeleteDeployment = useCallback(async () => {
    if (!toDelete) return;
    setDeleteOpen(false);
    try {
      await createPromiseApiAction(dispatch, DELETE_SCHEDULED_DEPLOYMENT, [
        [toDelete.id],
      ]);
    } catch (e) {
      console.error("Failed to delete deployment:", e);
    } finally {
      setToDelete(null);
    }
  }, [dispatch, toDelete]);

  const filteredRows = useMemo(() => {
    const { strategy, target } = filters;
    return rows.filter((deployment) => {
      if (strategy && deployment.strategy !== strategy) {
        return false;
      }
      if (target && target.length > 0) {
        const targetIds = new Set(deployment.targets.map((t) => String(t.id)));
        if (!target.every((tid) => targetIds.has(tid))) {
          return false;
        }
      }
      return true;
    });
  }, [filters, rows]);

  const filterOptions = useMemo(
    () => (
      <FiltersBar
        onChange={setFilters}
        strategyOptions={strategyOptions}
        targetOptions={targetOptions}
        value={filters}
      />
    ),
    [strategyOptions, targetOptions, filters],
  );

  return (
    <Container title="Scheduled Deployments">
      {storesLoaded ? (
        <DataTable
          columns={columns}
          filter={filter}
          initialPageSize={10}
          initialSort={{ id: "start_date", desc: true }}
          rows={filteredRows}
          viewOptions={filterOptions}
        />
      ) : (
        <LoadingIndicator />
      )}
      <DestructiveConfirmationDialog
        commitLabel="Delete"
        onCancel={() => closeDelete()}
        onCommit={handleDeleteDeployment}
        open={deleteOpen}
        title="Are you sure?"
        variant="modal"
      >
        This deployment will be deleted permanently.
      </DestructiveConfirmationDialog>
    </Container>
  );
}

const formatDateTime = (date: string | undefined): string => {
  if (!date) {
    return "N/A";
  }
  return utcToLocalFormat(date, DEFAULT_TIME_FORMAT_WITH_TZ);
};

const formatTargets = (targets: ReadonlyArray<Target>) => {
  const tags = targets.filter((t) => t.type === "t");
  const devices = targets.filter((t) => t.type === "d");

  if (tags.length === 0 && devices.length === 0) return <>N/A</>;

  return (
    <List dense disablePadding>
      {tags.map((tag, i) => (
        <ListItem disablePadding key={`t-${i}`}>
          <Link
            to={{
              pathname: "/devices",
              state: { tag: String(tag.id) },
            }}
          >
            {tag.name}
          </Link>
        </ListItem>
      ))}
      {devices.map((device, i) => (
        <ListItem disablePadding key={`d-${i}`}>
          <Link to={`/devices/${device.id}`}>{device.name}</Link>
        </ListItem>
      ))}
    </List>
  );
};

const FiltersBar = ({
  strategyOptions,
  targetOptions,
  value,
  onChange,
}: {
  strategyOptions: Option[];
  targetOptions: Option[];
  value: Filter;
  onChange: (newValue: Filter) => void;
}) => {
  return (
    <Stack alignItems="center" direction="row" spacing={1}>
      <Box width={300}>
        <Autocomplete
          inputProps={{ label: "Strategy Name" }}
          onChange={(_e, opt: Option | null) =>
            onChange({
              ...value,
              strategy: opt?.value == null ? null : String(opt.value),
            })
          }
          options={strategyOptions}
          size="small"
          value={
            strategyOptions.find((opt) => opt.value === value.strategy) || null
          }
        />
      </Box>
      <Box minWidth={300}>
        <Autocomplete
          disableCloseOnSelect
          getOptionLabel={(o: Option) => o.label}
          inputProps={{ label: "Targets" }}
          isOptionEqualToValue={(a, b) => a.value === b.value}
          limitTags={2}
          multiple
          onChange={(_e, opts: Option[] | null) =>
            onChange({
              ...value,
              target: opts ? opts.map((o) => String(o.value)) : [],
            })
          }
          options={targetOptions}
          renderOption={(props, option) => (
            <li {...props} key={option.value}>
              {option.label}
            </li>
          )}
          size="small"
          value={targetOptions.filter((o) =>
            (value.target ?? []).includes(String(o.value)),
          )}
        />
      </Box>
    </Stack>
  );
};
